/**
 * The Courses page, one list (12 Oct 2026 — design “Courses: everything you've made, and
 * everything assigned to you”). A card is a course family you made, a course assigned to you, or
 * both — merged by root id, so a course you made AND were assigned is ONE card, whose action is the
 * learning one (Continue) with Edit in its menu.
 *
 * Pure: the page fetches, this decides what is shown and in what order.
 */
import type { CourseGroup } from '@/lib/db'
import type { LearningItem } from '@/lib/orgApi'
import { isPublished, lastEdited } from '@/lib/courseListView'

export interface CourseEntry {
  key: string
  /** The family, when you can see it as a course (you made it, or it's shared with the team). */
  group: CourseGroup | null
  /** The assignment, when it is assigned to you. */
  learning: LearningItem | null
}

/** Where you are with an assigned course. */
export type LearnState = 'overdue' | 'todo' | 'inprogress' | 'completed'
export function learnState(i: LearningItem): LearnState {
  // A completed course being taken again reads as in progress — that attempt resumes like any course.
  if (i.progress?.retake) return 'inprogress'
  if (i.status === 'completed') return 'completed'
  if (i.status === 'overdue') return 'overdue'
  return i.progress && i.progress.status !== 'not_started' ? 'inprogress' : 'todo'
}
const started = (i: LearningItem) => !!i.progress && i.progress.status !== 'not_started'

export type CourseChip = 'all' | 'made' | 'forme' | 'todo' | 'inprogress' | 'completed' | 'drafts' | 'launched'
export const CHIP_LABEL: Record<CourseChip, string> = {
  all: 'All', made: 'Made by me', forme: 'For me', todo: 'To do', inprogress: 'In progress', completed: 'Completed', drafts: 'Drafts', launched: 'Launched',
}
/** The chips each view offers: your courses, the organisation's, or a Learner's (assigned only). */
export function chipsFor(view: 'mine' | 'org' | 'learner'): CourseChip[] {
  // Organisation: the team's courses, plus where you are with the ones assigned to you (15 Oct 2026).
  if (view === 'org') return ['all', 'launched', 'drafts', 'todo', 'inprogress', 'completed']
  if (view === 'learner') return ['all', 'todo', 'inprogress', 'completed']
  return ['all', 'made', 'forme', 'todo', 'inprogress', 'completed', 'drafts']
}

/** One card per course: your families, then what's assigned to you, merged by root id. */
export function buildEntries(groups: CourseGroup[], learning: LearningItem[]): CourseEntry[] {
  const byRoot = new Map(learning.map(i => [i.courseId, i]))
  const out: CourseEntry[] = groups.map(g => ({ key: g.root.id, group: g, learning: byRoot.get(g.root.id) ?? null }))
  const seen = new Set(groups.map(g => g.root.id))
  for (const i of learning) if (!seen.has(i.courseId)) out.push({ key: i.courseId, group: null, learning: i })
  return out
}

export function matchesChip(e: CourseEntry, chip: CourseChip, opts: { userId?: string | null; launched?: ReadonlySet<string> } = {}): boolean {
  const l = e.learning, g = e.group
  switch (chip) {
    case 'all': return true
    case 'made': return !!g && !!opts.userId && g.root.created_by === opts.userId
    case 'forme': return !!l
    // To do: not started yet. In progress: started, not finished. Overdue ones belong to both by the
    // same rule (20 Oct 2026) — a started course that went overdue used to match neither and showed
    // only under All. The card still says Overdue.
    // Completed means completed, even while it is being taken again (22 Oct 2026, Jasmine): a retake
    // moved it to In progress and out of the Completed count, though the course was already done.
    case 'todo': return !!l && l.status !== 'completed' && !started(l)
    case 'inprogress': return !!l && l.status !== 'completed' && started(l)
    case 'completed': return !!l && l.status === 'completed'
    case 'drafts': return !!g && !isPublished(g)
    case 'launched': return !!g && !!opts.launched?.has(g.root.id)
  }
}

export function chipCounts(entries: CourseEntry[], chips: CourseChip[], opts: Parameters<typeof matchesChip>[2] = {}): Record<CourseChip, number> {
  const out = {} as Record<CourseChip, number>
  for (const c of chips) out[c] = entries.filter(e => matchesChip(e, c, opts)).length
  return out
}

export const entryTitle = (e: CourseEntry) => (e.group?.root.title || e.learning?.title || 'Untitled Course').trim()

/** Search: any language version's title (as before), or the assigned course's titles. */
export function matchesQuery(e: CourseEntry, q: string): boolean {
  const s = q.trim().toLowerCase()
  if (!s) return true
  const titles = [...(e.group?.all.map(c => c.title ?? '') ?? []), e.learning?.title ?? '', ...(e.learning?.versions.map(v => v.title) ?? [])]
  return titles.some(t => t.toLowerCase().includes(s))
}

/**
 * “Needs you first”: what's late, then what you're in the middle of, then what you haven't
 * started (soonest due first), then what you made (last edited first), and what you've finished last.
 */
export function needsRank(e: CourseEntry): number {
  const l = e.learning
  if (l) {
    const s = learnState(l)
    if (s === 'overdue') return 0
    if (s === 'inprogress') return 1
    if (s === 'todo') return 2
  }
  if (e.group) return 3
  return 4
}
const t = (iso: string | null | undefined) => (iso ? Date.parse(iso) || 0 : 0)
export function compareNeeds(a: CourseEntry, b: CourseEntry): number {
  const r = needsRank(a) - needsRank(b)
  if (r) return r
  const rank = needsRank(a)
  if (rank === 1) return t(b.learning!.progress?.lastActiveAt) - t(a.learning!.progress?.lastActiveAt)
  if (rank === 0 || rank === 2) return (t(a.learning!.dueAt) || Infinity) - (t(b.learning!.dueAt) || Infinity) || t(b.learning!.receivedAt) - t(a.learning!.receivedAt)
  if (rank === 3) return lastEdited(b.group!) - lastEdited(a.group!)
  return t(b.learning?.progress?.completedAt) - t(a.learning?.progress?.completedAt)
}
